<template>
  <f7-block
    strong
    inset
    class="group-summary-card"
    :class="[
      variant,
      { collapsed: isAccordion && collapsed }
    ]"
  >
    <div class="summary-header">
      <f7-icon :f7="summaryIcon" :color="summaryColor" size="24" />
      <h3>{{ summaryTitle }}</h3>
      <f7-link
        v-if="isAccordion"
        class="accordion-toggle display-flex align-items-right"
        :aria-expanded="!collapsed"
        :aria-label="
          collapsed
            ? $t('groupSummary.accordion.show')
            : $t('groupSummary.accordion.hide')
        "
        @click="collapsed = !collapsed"
      >
        <f7-icon
          :f7="collapsed ? 'chevron_down' : 'chevron_up'"
          :color="'gray'"
          size="20"
        />
      </f7-link>
    </div>

    <p v-if="isAccordion && collapsed && miniSummary" class="summary-mini">
      {{ miniSummary }}
    </p>

    <div
      class="summary-body"
      :class="{ closed: isAccordion && collapsed }"
      :aria-hidden="isAccordion && collapsed ? 'true' : 'false'"
    >
      <div class="summary-body-inner">
        <p class="summary-description">
          {{ summaryDescription }}
        </p>

        <f7-list media-list>
          <f7-list-item
            v-for="(feature, index) in summaryFeatures"
            :key="index"
            :title="$t(feature.titleKey)"
            :footer="$t(feature.footerKey)"
          >
            <template #media>
              <f7-icon :f7="feature.icon" :color="feature.color" />
            </template>
          </f7-list-item>
        </f7-list>

        <div class="summary-footer">
          <f7-badge v-if="category" :color="summaryColor" class="type-badge">
            {{ $t(categoryLabel )}}
          </f7-badge>
          <div v-if="selectedTypeLabels.length" class="summary-types">
            <f7-badge
              v-for="label in selectedTypeLabels"
              :key="label"
              :color="summaryColor"
              class="type-chip"
            >
              {{ $t(label) }}
            </f7-badge>
          </div>
          <p class="text-color-gray">
            {{ footerNote }}
          </p>
        </div>
      </div>
    </div>
  </f7-block>
</template>

<script setup>
import { computed, ref } from 'vue'
import {
  GROUP_CATEGORIES,
  groupTypeLabel,
} from '../../constants/group-taxonomy.js'
import i18n from '../../js/i18n.js'
const { t } = i18n.global

const props = defineProps({
  categoryCode: {
    type: String,
    default: null
  },
  types: {
    type: Array,
    default: () => []
  },
  step: {
    type: Number,
    default: null
  },
  variant: {
    type: String,
    default: 'full'
  }
})

// 'full'  → always expanded, static (tamtam.vue)
// 'accordion' → collapsed sticky bar on mobile, expandable
// 'rail'  → always expanded, sticky desktop sidebar
const isAccordion = computed(() => props.variant === 'accordion')
const collapsed = ref(true)

const category = computed(() =>
  GROUP_CATEGORIES.find((c) => c.code === props.categoryCode)
)

const categoryLabel = computed(() => category.value?.label || '')

const selectedTypeLabels = computed(() =>
  (props.types || []).map(groupTypeLabel).filter(Boolean)
)

const iconMap = {
  heart: 'heart_fill',
  school: 'graduationcap_fill',
  'building-church': 'building_2_fill',
  world: 'globe',
  briefcase: 'briefcase_fill',
  'ball-football': 'sportscourt_fill',
  'hand-heart': 'hand_raised_fill'
}

const colorMap = {
  SOCIAL_FAMILY: 'blue',
  EDUCATION_ALUMNI: 'purple',
  RELIGIOUS_SPIRITUAL: 'teal',
  CULTURAL_IDENTITY: 'pink',
  WORK_BUSINESS: 'green',
  LEISURE_SPORT: 'orange',
  ASSOCIATION_SOLIDARITY: 'red'
}

const F = 'groupSummary.features.'
const baseFeatures = [
  { icon: 'chat_bubble_fill', titleKey: F + 'communication.title', footerKey: F + 'communication.desc', color: 'blue' },
  { icon: 'doc_text_fill', titleKey: F + 'documents.title', footerKey: F + 'documents.desc', color: 'green' }
]

const CATEGORY_FEATURES = {
  SOCIAL_FAMILY: [
    { icon: 'wallet_fill', titleKey: F + 'cagnotte.title', footerKey: F + 'cagnotte.desc', color: 'green' },
    { icon: 'calendar_fill', titleKey: F + 'events.title', footerKey: F + 'events.desc', color: 'orange' }
  ],
  EDUCATION_ALUMNI: [
    { icon: 'chart_bar_fill', titleKey: F + 'promoFund.title', footerKey: F + 'promoFund.desc', color: 'green' },
    { icon: 'person_2_fill', titleKey: F + 'directory.title', footerKey: F + 'directory.desc', color: 'blue' }
  ],
  RELIGIOUS_SPIRITUAL: [
    { icon: 'heart_fill', titleKey: F + 'donations.title', footerKey: F + 'donations.desc', color: 'red' },
    { icon: 'building_2_fill', titleKey: F + 'communityProjects.title', footerKey: F + 'communityProjects.desc', color: 'teal' }
  ],
  CULTURAL_IDENTITY: [
    { icon: 'globe', titleKey: F + 'culturalEvents.title', footerKey: F + 'culturalEvents.desc', color: 'pink' },
    { icon: 'arrow_2_circlepath', titleKey: F + 'diaspora.title', footerKey: F + 'diaspora.desc', color: 'green' }
  ],
  WORK_BUSINESS: [
    { icon: 'bag_fill', titleKey: F + 'marketplace.title', footerKey: F + 'marketplace.desc', color: 'orange' },
    { icon: 'briefcase_fill', titleKey: F + 'managementTools.title', footerKey: F + 'managementTools.desc', color: 'green' }
  ],
  LEISURE_SPORT: [
    { icon: 'sportscourt_fill', titleKey: F + 'sportsEvents.title', footerKey: F + 'sportsEvents.desc', color: 'orange' },
    { icon: 'wallet_fill', titleKey: F + 'dues.title', footerKey: F + 'dues.desc', color: 'blue' }
  ],
  ASSOCIATION_SOLIDARITY: [
    { icon: 'arrow_2_circlepath', titleKey: F + 'tontine.title', footerKey: F + 'tontine.desc', color: 'green' },
    { icon: 'hand_raised_fill', titleKey: F + 'mutualAid.title', footerKey: F + 'mutualAid.desc', color: 'red' }
  ]
}

const genericFeatures = [
  { icon: 'person_2_fill', titleKey: F + 'privateSpace.title', footerKey: F + 'privateSpace.desc', color: 'green' },
  { icon: 'chat_bubble_fill', titleKey: F + 'groupChat.title', footerKey: F + 'groupChat.desc', color: 'blue' },
  { icon: 'doc_text_fill', titleKey: F + 'sharedDocs.title', footerKey: F + 'sharedDocs.desc', color: 'orange' }
]

const summaryTitle = computed(() =>
  category.value
    ? t('groupSummary.title.withCategory', { category: t(category.value.label) })
    : t('groupSummary.title.generic')
)

const summaryIcon = computed(() =>
  category.value ? iconMap[category.value.icon] || 'people_fill' : 'people_fill'
)

const summaryColor = computed(() =>
  category.value ? colorMap[category.value.code] || 'blue' : 'blue'
)

const summaryFeatures = computed(() => {
  if (category.value) {
    return [...baseFeatures, ...(CATEGORY_FEATURES[category.value.code] || [])]
  }
  return genericFeatures
})

const summaryDescription = computed(() => {
  if (category.value) {
    return t(`groupSummary.descriptions.${category.value.code}`)
  }
  return t('groupSummary.descriptions.generic')
})

const footerNote = computed(() => {
  if (
    props.step &&
    props.step >= 2 &&
    selectedTypeLabels.value.length
  ) {
    return t('groupSummary.footer.withTypes', {
      types: selectedTypeLabels.value.join(', '),
    })
  }
  if (category.value) {
    return t('groupSummary.footer.withCategory')
  }
  return t('groupSummary.footer.generic')
})

const miniSummary = computed(() => {
  if (selectedTypeLabels.value.length) {
    const translated = selectedTypeLabels.value.slice(0, 2).map(key => t(key))
    return translated.join(', ')
  }
  return category.value?.label ? t(category.value.label) : ''
})
</script>

<style scoped>
.group-summary-card {
  margin-top: 20px;
  background: var(--f7-card-bg-color, #fff);
  color: var(--f7-on-surface, #161d18);
}

/* Desktop rail: sits directly in the sticky sidebar, no page padding. */
.group-summary-card.rail {
  margin-top: 0;
}

/* Mobile accordion: slim bar sticks under the navbar while collapsed,
   and scrolls away (static) once expanded to free the screen. */
.group-summary-card.accordion {
  margin-top: 8px;
}
.group-summary-card.accordion.collapsed {
  position: sticky;
  top: 10px;
  z-index: 6;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0;
}
.accordion-toggle {
  margin-left: auto;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  border-radius: 50%;
}
.accordion-toggle:focus-visible {
  outline: 2px solid var(--f7-theme-color);
  outline-offset: 2px;
}
.summary-header h3 {
  margin: 0;
  font-size: 18px;
}
.summary-mini {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--f7-on-surface-variant, #8e8e93);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

/* Collapsible body (only animated for the accordion variant). */
.summary-body {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.3s ease;
}
.summary-body.closed {
  grid-template-rows: 0fr;
}
.summary-body-inner {
  min-height: 0;
  overflow: hidden;
}
.summary-body.closed .summary-description {
  display: none;
}

.summary-description {
  font-size: 14px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 12px 0 16px;
  line-height: 1.4;
}
.summary-footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--f7-list-item-border-color, rgba(0, 0, 0, 0.08));
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.type-badge {
  font-size: 12px;
}
.summary-types {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.type-chip {
  font-size: 11px;
  font-weight: 500;
  padding: 3px 9px;
}
.summary-footer p {
  margin: 0;
  font-size: 12px;
}
</style>